import { GymTypes, TimeSpan } from 'fitbeat.models';
import { WorkoutSettings } from '../../entities/user/workoutsettings';
import { WorkoutSelection } from '../../entities/workout/workoutselection';
import { UserWorkoutSession } from '../../entities/workoutsession/userworkoutsession';
import { ILastWorkoutWorkoutSelectionId, IWorkoutRepository } from '../../persistence/repositories';
import {
  CurrentWorkoutSelectionManager,
  IWorkoutSelectionRange,
  WorkoutSelectionManager,
} from '../workoutselection';
import { IWorkoutSelectionStrategy } from './IWorkoutSelectionStrategy';

export class InfluencerWorkoutSelectionStrategy
  implements IWorkoutSelectionStrategy {
  private workoutSelctionIdRange: IWorkoutSelectionRange;

  constructor(
    private workoutRepository: IWorkoutRepository,
    private currentWorkoutSelectionManager: CurrentWorkoutSelectionManager,
    private workoutSelectionManager: WorkoutSelectionManager,
  ) {
    this.workoutSelctionIdRange = { lowerLimit: 25, upperLimit: 30 };
  }

  public async getWorkoutSelectionForWorkout(
    settings: WorkoutSettings,
    gymType: GymTypes,
    requestTime: Date,
    timezone: string,
    userWorkoutSession?: UserWorkoutSession,
  ): Promise<any> {
    if (!userWorkoutSession) {
      throw new Error('user workout session is required');
    }
    const lastWorkoutSelection =
      await this.workoutRepository.getLastWorkoutWorkoutSelectionId(
        userWorkoutSession.userProfileId,
        settings,
        gymType,
        true,
      );
    await this.setWorkoutSelectionId(settings, userWorkoutSession, requestTime, timezone || '', lastWorkoutSelection);
    const activeWorkoutSelection = await this.getActiveWorkoutSelectionFromId(
      userWorkoutSession.workoutSelectionId,
    );
    return activeWorkoutSelection;
  }

  private getcurrentSelectionNumber = (
    previousWorkoutSelection: {
        startTime: Date,
        workoutSelectionIndex: number,
        workoutSelectionKey: string,
      } | undefined,
  ): number => {
    if (!previousWorkoutSelection) {
      return this.workoutSelctionIdRange.upperLimit;
    }
    const { workoutSelectionIndex }  = previousWorkoutSelection;
    if (
      workoutSelectionIndex < this.workoutSelctionIdRange.lowerLimit ||
      workoutSelectionIndex > this.workoutSelctionIdRange.upperLimit
    ) {
      return this.workoutSelctionIdRange.upperLimit;
    }
    return workoutSelectionIndex;
  }

  private async setWorkoutSelectionId(
    workoutSettings: WorkoutSettings,
    workoutSession: UserWorkoutSession,
    startTime: Date,
    timezone: string,
    previousWorkoutSelection?: ILastWorkoutWorkoutSelectionId,
  ) {
    const previousSelectionIsOnTheSameDay =
      previousWorkoutSelection &&
      TimeSpan.areOnSameDayInTimeZone(
        previousWorkoutSelection.startTime,
        startTime,
        timezone,
      );

    if (previousWorkoutSelection && previousSelectionIsOnTheSameDay) {
      workoutSession.workoutSelectionId =
        `${previousWorkoutSelection.workoutSelectionIndex}_${previousWorkoutSelection.workoutSelectionKey}`;
    } else {
      const currentSelectionNumber = this.getcurrentSelectionNumber(previousWorkoutSelection);
      const workoutSelection =
        await this.currentWorkoutSelectionManager.determineNextWorkoutSelection(
          currentSelectionNumber,
          GymTypes.Home,
          workoutSettings,
          this.workoutSelctionIdRange,
        );
      if (!workoutSelection) {
        throw new Error(`No workout selection found`);
      }
      workoutSession.workoutSelectionId = workoutSelection.uniqueReference;
    }
  }

  private async getActiveWorkoutSelectionFromId(
    workoutSelectionId: string,
  ): Promise<WorkoutSelection | undefined> {
    const workoutSelection =
      await this.currentWorkoutSelectionManager.getSelectionForReferenceNumber(
        workoutSelectionId,
      );
    if (!workoutSelection) {
      throw new Error(`No workout selection found for ${workoutSelectionId}`);
    }
    await this.workoutSelectionManager.populateExercises(
      workoutSelection.exerciseSelections,
    );
    return workoutSelection;
  }
}
